<script lang="ts" setup>
import PaginatedTable from '@/app/components/PaginatedTable.vue'
import { ref, onMounted, watch } from 'vue'
import { useField, useForm } from 'vee-validate'
import { object, string, array } from 'yup'
import { useRouter } from 'vue-router'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { formatNumber } from '@/app/core/util/numbers'
import { useI18n } from 'vue-i18n'
import {
  DCard,
  DBtn,
  DSelectFieldSearch,
  DLoading,
  DTextField,
  DTextAreaField,
  DEmployeesFilter,
  FilterLinesIcon,
  Users01Icon
} from 'front-library'
import {
  getEmployees,
  getListIncreaseReason,
  getListIncreaseType,
  IncreaseSalaryRequest,
  IncreaseSalaryPreview,
  previewIncreaseSalaryRequest,
  createIncreaseSalaryRequest
} from '@/app/core/services/increase-salary-requests'
usePayrollChange(() => window.location.reload())

/* router, notifications */
const router = useRouter()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente table */
const columns = ref([
  { key: 'employee', title: t('increaseSalary.list.employee') },
  { key: 'currency', title: t('increaseSalary.list.currency') },
  {
    key: 'oldSalary',
    title: t('increaseSalary.list.oldSalary'),
    format: (value: any) => formatNumber(value)
  },
  {
    key: 'newSalary',
    title: t('increaseSalary.list.newSalary'),
    format: (value: any) => formatNumber(value)
  }
])
const rows = ref<IncreaseSalaryPreview[]>([])

/* Datos Listas */
const itemsIncreaseReason = ref<any[]>([])
const itemsIncreaseType = ref<object[]>([])
const itemsEmployees = ref<string[]>([])

/* Datos componente DLoading */
const loading = ref(false)
const messageLoading = ref('')

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  increaseReasonId: string().required(
    t('form.valid.fieldRequired', { field: t('increaseSalary.valid.reason') })
  ),
  incrementSalaryTypeId: string().required(
    t('form.valid.fieldRequired', {
      field: t('increaseSalary.valid.increaseType')
    })
  ),
  value: string().required(
    t('form.valid.fieldRequired', { field: t('increaseSalary.valid.value') })
  ),
  appliesFrom: string().required(
    t('form.valid.fieldRequired', {
      field: t('increaseSalary.valid.appliesFrom')
    })
  ),
  observation: string()
    .max(300)
    .required(
      t('form.valid.fieldRequired', {
        field: t('increaseSalary.valid.observations')
      })
    ),
  employees: array()
    .min(1, t('increaseSalary.valid.employeeSelectionRequired'))
    .required(
      t('form.valid.fieldRequired', {
        field: t('increaseSalary.valid.employees')
      })
    )
})

/* Uso del metodo de validaión, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<IncreaseSalaryRequest>({
  validationSchema
})

/* Definicion de  campos del formulario */
const { value: increaseReasonId } = useField<string>(ref('increaseReasonId'))
const { value: incrementSalaryTypeId } = useField<string>(
  ref('incrementSalaryTypeId')
)
const { value: appliesFrom } = useField<string>(ref('appliesFrom'))
const { value } = useField<string>(ref('value'))
const { value: employees } = useField<any[]>(ref('employees'))
const { value: observation } = useField<string>(ref('observation'))
watch(appliesFrom, () => {
  void listEmployees(appliesFrom.value)
})

/* Metodo que valida los campos obligatorios del formulario */
const onPreview = handleSubmit((values: any, { resetForm }) => {
  rows.value = []
  void previewIncreaseSalary(values)
})
const onSubmit = handleSubmit((values: any, { resetForm }) => {
  void startIncreaseSalary(values)
})
const previewIncreaseSalary = async (values: any) => {
  loading.value = true
  messageLoading.value = t('increaseSalary.msgLoading.msg1')
  try {
    const data: any = await previewIncreaseSalaryRequest(values)
    rows.value = data
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'increaseSalary', t, notifications, loading)
  }
}

const startIncreaseSalary = async (values: any) => {
  loading.value = true
  messageLoading.value = t('increaseSalary.msgLoading.msg2')
  try {
    await createIncreaseSalaryRequest(values)
    notifications.addNotification({
      title: t('increaseSalary.notification.processStarted'),
      text: t('increaseSalary.notification.processSuccess'),
      type: 'success',
      time: 10000
    })
    loading.value = false
    void router.push({ name: 'ListIncreaseSalaryRequests' })
  } catch (error: any) {
    notificationError(error, 'increaseSalary', t, notifications, loading)
    await router.push({ name: 'ListIncreaseSalaryRequests' })
  }
}

/* Listar empleados */
const listEmployees = async (appliesFrom: any) => {
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    itemsEmployees.value = await getEmployees(appliesFrom)
    employees.value = itemsEmployees.value.map((_: any) => _.id)
    loading.value = false
  } catch (error) {
    loading.value = false
  }
}

/* Volver */
const back = () => {
  void router.push({ name: 'ListIncreaseSalaryRequests' })
}

onMounted(async () => {
  loading.value = true
  try {
    ;[itemsIncreaseReason.value, itemsIncreaseType.value] = await Promise.all([
      getListIncreaseReason(),
      getListIncreaseType()
    ])
    loading.value = false
  } catch (error: any) {
    if (error.response.status === 400) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('increaseSalary.notification.error'),
        type: 'error',
        time: 10000
      })
      void back()
    } else {
      notificationError(error, 'increaseSalary', t, notifications, loading)
    }
  }
})
</script>
<template>
  <div class="h-full px-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <div class="bg-gray-50 pb-10">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('increaseSalary.create')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 13rem)">
      <DCard>
        <div class="grid grid-cols-3">
          <div class="col-span-4 lg:col-span-2 pl-2 pr-4 py-2">
            <div class="grid grid-cols-6 gap-3">
              <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                <DSelectFieldSearch
                  :label="t('increaseSalary.fields.reason')"
                  :required="true"
                  v-model="increaseReasonId"
                  :valueKey="'id'"
                  :options="itemsIncreaseReason"
                  :error="errors.increaseReasonId" />
              </div>
              <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                <DSelectFieldSearch
                  :label="t('increaseSalary.fields.increaseType')"
                  :required="true"
                  v-model="incrementSalaryTypeId"
                  :options="itemsIncreaseType"
                  :error="errors.incrementSalaryTypeId" />
              </div>
              <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                <DTextField
                  type="text"
                  :label="t('increaseSalary.fields.value')"
                  v-model="value"
                  :required="true"
                  :error="errors.value" />
              </div>
              <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                <DTextField
                  type="date"
                  :label="t('increaseSalary.fields.appliesFrom')"
                  v-model="appliesFrom"
                  :required="true"
                  :error="errors.appliesFrom" />
              </div>
              <div class="col-span-6 sm:col-span-6 lg:col-span-6">
                <DTextAreaField
                  :label="t('increaseSalary.fields.observations')"
                  v-model="observation"
                  :required="true" />
              </div>
            </div>
          </div>
          <div
            class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
            <div class="px-4 flex justify-between">
              <h3 class="text-md font-medium leading-6 text-gray-900">
                {{ t('increaseSalary.fields.employees') }}
              </h3>
              <h3
                class="text-md font-medium leading-6 text-gray-900 flex items-center">
                <DEmployeesFilter :items="itemsEmployees" v-model="employees" />
                <FilterLinesIcon class="w-8" />
              </h3>
            </div>
            <div class="flex justify-center items-center p-5 gap-5">
              <Users01Icon class="w-20 h-20" />
              <h1 class="text-5xl font-medium leading-6 text-gray-900">
                {{
                  `${employees?.length === undefined ? 0 : employees?.length}/${
                    itemsEmployees?.length
                  }`
                }}
              </h1>
            </div>
            <div class="text-xs text-red-500 self-center">
              {{ errors.employees }}
            </div>
          </div>
          <hr class="col-span-3 mb-4 mt-3" />
          <div class="col-span-3 px-5">
            <div class="col-span-3 flex justify-start">
              <DBtn
                @click="onPreview"
                v-html="t('increaseSalary.fields.preview')" />
            </div>
            <PaginatedTable
              :columns="columns"
              :rows="rows"
              height-table="calc(100vh - 19rem)" />
          </div>
          <hr class="col-span-3 mb-4 mt-5" />
          <div class="col-span-3 flex justify-end">
            <DBtn
              color="secondary"
              class="mr-3"
              @click="back"
              v-html="t('form.button.cancel')" />
            <DBtn @click="onSubmit" v-html="t('form.button.createRequest')" />
          </div>
        </div>
      </DCard>
    </form>
  </div>
</template>
